// Product builder — turns a generated Asset (from Create Your Asset) into
// downloadable digital products for each tier of the offer ladder.
// Uses the user's OpenAI key (gpt-4o-mini for text, gpt-image-1 for covers).

import { chatText, generateImage } from "./openai";

// ─────────────────────────────────────────────────────────────────────
// Shared brand bible derived from the offer — keeps all 4 tiers cohesive.
// ─────────────────────────────────────────────────────────────────────
function brandBible(offerData) {
  const d = offerData || {};
  return [
    `Niche: ${d.niche || "—"}`,
    `Target audience: ${d.target_audience?.who || "—"} | Lifestyle: ${d.target_audience?.lifestyle || "—"}`,
    `Core pain: ${d.core_problem?.pain_point || "—"} | Emotional frustration: ${d.core_problem?.emotional_frustration || "—"}`,
    `What they're searching for: ${d.searching_for?.intent || "—"}`,
    `Positioning statement: ${d.positioning_statement || "—"}`,
    `Product packaging: ${d.digital_product?.positioning_and_packaging || "—"}`,
    `Central product idea: ${d.digital_product?.idea || "—"}`,
    `Product format: ${d.digital_product?.format || "—"}`,
  ].join("\n");
}

// ─────────────────────────────────────────────────────────────────────
// Per-tier spec — what to generate for each rung of the ladder.
// ─────────────────────────────────────────────────────────────────────
export const TIERS = {
  free_lead_magnet: {
    key: "free_lead_magnet",
    label: "Free Lead Magnet",
    badge: "Free",
    size: "3–5 page checklist / cheat-sheet",
    wordTarget: 900,
    description: "A quick-win checklist or cheat-sheet readers finish in under 10 minutes — designed to capture email and warm them up for the low-ticket.",
    sections: [
      "Promise + who it's for (1 short paragraph)",
      "The ONE outcome this unlocks (1–2 lines, specific)",
      "The Checklist (10–15 tight, actionable bullets — each < 12 words)",
      "Quick Wins (3 fastest things to do TODAY)",
      "What comes next (soft CTA to the Low-Ticket tier — by name)",
    ],
  },
  low_ticket: {
    key: "low_ticket",
    label: "Low-Ticket",
    badge: "$22–$49",
    size: "15–25 page ebook / playbook + bonus stack",
    wordTarget: 5800,
    description: "The core paid entry-point — an ebook / playbook plus a bundled stack (Fast Start Kit, Swipe Library, Mistakes Guide, AI Toolkit, Quick Wins, Implementation System). Designed to feel like a complete transformation ecosystem, not a single ebook.",
    sections: [
      "Cover page (title, subtitle, ONE-line promise — NO byline)",
      "Foreword / Why this matters (300 words, story-driven, concrete details)",
      "How to use this asset (bullet list, 6 bullets)",
      "Chapter 1 — Reframe the problem (800 words, contrarian, names the real friction)",
      "Chapter 2 — The system / method (1000 words, step-by-step, NAMED mechanism)",
      "Chapter 3 — Common mistakes (600 words, 5 NAMED mistakes with fixes + cost of getting it wrong)",
      "Chapter 4 — Your first 7 days (600 words, day-by-day plan with the actual daily action)",
      "BONUS · Fast Start Kit (1-page concrete starter checklist — 7-10 actions a buyer can complete in their first 24 hours)",
      "BONUS · Swipe Library (10 ready-to-use lines / templates / scripts / prompts pulled from the chapters — copy-paste, NAMED specifically for this niche)",
      "BONUS · Mistakes Guide (the 5 named mistakes from Chapter 3 expanded into a 1-page reference card)",
      "BONUS · AI Toolkit (5 ready-to-paste AI prompts the buyer can run today — niche-specific, not generic)",
      "BONUS · Quick Wins (3 sub-30-minute wins they can stack TODAY — concrete numbers / actions)",
      "BONUS · Implementation System (a 1-page 4-week tracker / cadence / habit loop — names the days, the actions, the metric)",
      "Resources & templates (bullet list of every bonus included above)",
      "Next step (CTA to Premium tier by name, with 1-line reason to upgrade)",
    ],
  },
  premium: {
    key: "premium",
    label: "Premium",
    badge: "$97–$299",
    size: "Mini-course + complete bonus stack",
    wordTarget: 8500,
    description: "A structured mini-course: 5 modules with lesson scripts and workbooks, plus the full bonus stack (Fast Start Kit, Swipe Library, Mistakes Guide, AI Toolkit, Quick Wins, Implementation System). Feels like a complete transformation ecosystem.",
    sections: [
      "Course title, promise, outcome, ideal student",
      "Curriculum at a glance (table-of-contents style)",
      "Module 1 — Foundations (lesson script ~500 words, workbook prompts x5)",
      "Module 2 — The Core Method (lesson script ~700 words, workbook prompts x5)",
      "Module 3 — Execution System (lesson script ~700 words, workbook prompts x5)",
      "Module 4 — Scaling / Compounding (lesson script ~600 words, workbook prompts x5)",
      "Module 5 — Advanced Plays / Case Studies (lesson script ~700 words, workbook prompts x5)",
      "BONUS · Fast Start Kit (concrete 1-page starter checklist — 10 actions for the first 48 hours)",
      "BONUS · Swipe Library (15 niche-specific scripts / templates / prompts ready to paste)",
      "BONUS · Mistakes Guide (10 named mistakes + the precise fix + the cost of getting it wrong)",
      "BONUS · AI Toolkit (10 ready-to-paste AI prompts that operationalise the modules)",
      "BONUS · Quick Wins (5 sub-60-minute wins to stack the first week)",
      "BONUS · Implementation System (12-week cadence + tracker — names the days, the actions, the metrics)",
      "Graduation checklist (10 items, each specific)",
      "Upsell to High-Ticket (1 paragraph, specific)",
    ],
  },
  high_ticket: {
    key: "high_ticket",
    label: "High-Ticket",
    badge: "$500+",
    size: "Coaching / done-for-you + full bonus stack",
    wordTarget: 7500,
    description: "A coaching / done-for-you program. Curriculum, weekly agenda, onboarding kit, deliverables, the full bonus stack, plus a ready-to-paste sales page.",
    sections: [
      "Program name, promise, typical timeline",
      "Who this is for / who it's NOT for (short, direct)",
      "Weekly agenda (Weeks 1–8 at minimum, 2–3 line bullets, names the action of each week)",
      "Deliverables per phase (onboarding, build, launch, scale)",
      "Client onboarding kit (welcome email, kickoff call agenda, intake questions x10)",
      "Communication cadence (what's included — Slack/Loom/calls)",
      "BONUS · Fast Start Kit (Week-0 concrete 1-page starter checklist — 10 actions in the first 48 hours of program enrolment)",
      "BONUS · Swipe Library (20 niche-specific scripts / templates / outreach DMs / sales pages — copy-paste)",
      "BONUS · Mistakes Guide (10 expensive mistakes + named fix + cost of getting it wrong)",
      "BONUS · AI Toolkit (15 high-leverage AI prompts that operationalise the program)",
      "BONUS · Quick Wins (10 fast wins for the first 30 days — concrete metric per win)",
      "BONUS · Implementation System (12-week tracker + cadence + accountability loop)",
      "Guarantee & refund policy (specific, not vague)",
      "Application questions (8 qualifying questions)",
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────
// Sales page prompt (always generated alongside the product).
// ─────────────────────────────────────────────────────────────────────
function salesPageInstruction(tier, offerData) {
  return `
SALES PAGE — output as a separate markdown section titled "## Sales Page Copy" that contains:
- Pre-headline (1 short line)
- Headline (≤ 12 words, scroll-stopping, specific outcome)
- Subheadline (1 sentence, expand the promise)
- 5 benefit bullets (outcomes, not features — each starts with a strong verb)
- Who it's for / who it's not for (2 short lists)
- What's included (bullet list of actual deliverables)
- Price frame (anchor → actual price, using: ${offerData?.pricing?.[tier.key]?.price_usd ? `$${offerData.pricing[tier.key].price_usd}` : "the tier price"})
- Guarantee (specific; prefer a conditional guarantee over vague "money back")
- CTA button text (≤ 5 words, action-first)
- Objection-handler FAQ (3 Q&As — pick the real objections a skeptical buyer has)

ZERO PLACEHOLDERS in the sales page either. Every value (price, timeframe, deliverable, guarantee terms, CTA text) must be a finished concrete string. NO "[Your Name]", NO "[Your Brand]", NO "<your link>", NO bracket-style fillers anywhere.`;
}

// ─────────────────────────────────────────────────────────────────────
// Build ONE tier's product — returns { markdown, cover (dataUrl) }
// ─────────────────────────────────────────────────────────────────────
export async function buildTierProduct({ apiKey, tierKey, offerData, onProgress = () => {} }) {
  const tier = TIERS[tierKey];
  if (!tier) throw new Error(`Unknown tier: ${tierKey}`);
  if (!apiKey) throw new Error("Missing OpenAI API key");

  onProgress({ stage: "writing", label: `Writing ${tier.label}…`, progress: 10 });

  const bible = brandBible(offerData);
  const sectionsList = tier.sections.map((s, i) => `${i + 1}. ${s}`).join("\n");
  const tierPriceNote = offerData?.pricing?.[tier.key]?.price_usd
    ? `Price: $${offerData.pricing[tier.key].price_usd} (${offerData.pricing[tier.key].note || ""})`
    : "";

  const system = `You are a senior direct-response product writer + digital-product designer who has shipped 100+ successful low-ticket and high-ticket info products. You write like a human expert: specific, first-person-ish, concrete, no corporate fluff, no AI-tells. You obey the brand bible literally — never invent niches, audiences, or promises outside of it. You ship PRODUCTION-READY content — never drafts, never placeholders, never "fill in later" markers. Output valid Markdown ONLY.`;

  const user = `Build the full ${tier.label.toUpperCase()} product in Markdown. The output must be a finished, ready-to-sell digital asset — not a draft, not a template, not a skeleton.

=== BRAND BIBLE (share across every tier — do NOT contradict) ===
${bible}
${tierPriceNote}
=== END BRAND BIBLE ===

PRODUCT TIER: ${tier.label}
SIZE: ${tier.size}
WORD TARGET (approx): ${tier.wordTarget}
PURPOSE: ${tier.description}

STRUCTURE (produce EVERY section, in this exact order, as a top-level "#" heading or "##" sub-headings inside the document):
${sectionsList}

PRODUCTION-READY RULES (NON-NEGOTIABLE):
- Open with a single "# <Product Title>" line. Make the title specific, ≤ 8 words, promise-forward (e.g. "The 14-Day Gut-Reset Playbook" — never "A Comprehensive Guide to…").
- Under the title, put ONE 1-line subtitle that names the concrete outcome. Then leave it. Do NOT add a byline, an author block, a "by [name]" line, or any metadata block.
- Use short paragraphs (2–4 sentences) and plenty of tight bullet lists.
- Include at least 3 CONCRETE specifics per major section (numbers, durations, dollar amounts, real-world scenarios pulled from the niche).
- Every framework / step / module MUST have a real NAMED MECHANISM (e.g. "The 3-Lever System", "The 90-Minute Reset", "The 7-Touch Sequence"). No anonymous "Step 1 / Step 2 / Step 3" filler.
- Every checklist / list item MUST be specific and actionable on its own — no placeholder verbs like "consider", "think about", "explore".

ZERO PLACEHOLDER POLICY (HARD BAN — if you violate this the output is rejected):
- NEVER write any of these or anything resembling them: "[Your Name]", "[Insert Name]", "[Author]", "[Creator]", "[Your Brand]", "[Company]", "[Your Logo]", "[Your URL]", "[Your Email]", "[Insert X]", "<your X>", "<insert X>", "TODO", "TBD", "Lorem ipsum", "fill in", "your business name", "your company".
- NEVER leave any value unresolved with brackets, angle brackets, or fill-in markers. Every value must be a real, finished string.
- If you don't have a specific real value, REPHRASE so the placeholder is unnecessary. Examples:
    × WRONG: "Welcome to [Your Program]"
    ✓ RIGHT: "Welcome to the playbook"
    × WRONG: "Email me at [your@email.com]"
    ✓ RIGHT: "Reply to the welcome email when you're ready for module two"
- Same rule applies to dollar amounts, dates, and timeframes — write a real specific number, never a bracketed placeholder.

ZERO HALLUCINATED CLAIMS POLICY (HARD BAN — destroys buyer trust):
- NEVER include fake income promises, guaranteed-results claims, "proven to make $X", "students earn $Y", "100% guaranteed", "guaranteed income", "guaranteed success", "results guaranteed".
- NEVER fabricate testimonials, quotes, or "case study" numbers unless the brand bible explicitly provides them.
- NEVER invent earnings data, student-count statistics, or success-rate percentages.
- If you want to discuss outcomes, use RANGE or POSSIBILITY language grounded in reality ("people in this situation typically…", "the realistic outcome is…", "your mileage will vary based on…") — never absolute claims.
- The guarantee section uses CONDITIONAL language ("If you complete the first 3 chapters and don't see [specific concrete benefit], write back and I'll refund you") — never vague "satisfaction guaranteed" or "results guaranteed".

QUALITY BAR — this product must feel like a complete transformation ECOSYSTEM, not a single ebook:
- Every section opens with a clear promise and ends with a concrete next action.
- Frameworks include a NAME, the steps, and at least one worked example.
- "Common mistakes" sections name the mistake + the precise fix + the cost of getting it wrong.
- Day-by-day or week-by-week plans give the actual action for each day, not a vague theme.
- Sales-page copy speaks in the buyer's language pulled from the brand bible.

PACKAGING SYSTEM — paid tiers MUST include the bonus stack as full sections:
The structure already lists each bonus by name (Fast Start Kit · Swipe Library · Mistakes Guide · AI Toolkit · Quick Wins · Implementation System). Treat these as FIRST-CLASS deliverables, not afterthoughts:
  · Fast Start Kit       → a real 1-page checklist of 7-10 concrete actions for the first 24-48 hours after purchase. Each action is specific (not "review your goals" — write "open a Notion doc, list your last 5 sleep times in minutes, average them — that's your baseline").
  · Swipe Library        → real ready-to-paste lines / templates / scripts / DMs / prompts. Each one fully written, named for THIS niche, immediately usable.
  · Mistakes Guide       → 5–10 named mistakes with the precise fix + the cost of getting it wrong. No generic "don't give up" filler.
  · AI Toolkit           → real, ready-to-paste AI prompts (system + user) the buyer can run today. Each one operationalises a specific module / chapter — never a generic "ChatGPT prompt for X".
  · Quick Wins           → 3–10 sub-60-minute wins with a concrete metric per win.
  · Implementation System→ a 4–12 week tracker / cadence / habit loop. Names the days, the actions, the metric, the success criteria.
The bonuses must feel useful, specific, emotionally relevant, immediately actionable, and conversion-enhancing. They are positioned as a "complete transformation ecosystem" — not bullet points. Format each bonus as a "##" sub-section in the document so the reader visually feels the size of the package.

GLOBAL STYLE RULES:
- Replace any generic placeholder like "<your niche>" with the actual niche from the brand bible.
- Do NOT include disclaimers, legal copy, or affiliate language — the user will add those separately.
- Do NOT repeat the brand bible back to the reader verbatim; weave it into the content.
- BANNED phrases: "in today's world", "unlock your potential", "game-changer", "dive in", "elevate", "empower", "journey", "at the end of the day", "rest assured", "level up".

${salesPageInstruction(tier, offerData)}

OUTPUT: Return ONLY the complete Markdown document. No preamble, no closing remarks, no code fences around the whole thing.`;

  const rawMarkdown = await chatText({
    apiKey,
    system,
    user,
    temperature: 0.85,
    maxTokens: 16000,
  });
  const markdown = stripPlaceholders(rawMarkdown);

  onProgress({ stage: "cover", label: `Designing ${tier.label} cover…`, progress: 70 });

  // Build a cover that actually reflects THIS niche + audience (not nomad archetypes).
  const title = extractTitle(markdown) || `${offerData?.niche || "Your"} ${tier.label}`;
  const niche = offerData?.niche || "digital product";
  const audience = offerData?.target_audience?.who || "busy adult audience";

  // Per-tier visual archetype — different COMPOSITION per rung so the 4 covers look
  // like a cohesive product family while remaining visually distinct.
  const TIER_SCENES = {
    free_lead_magnet: `NICHE-SPECIFIC flat-lay editorial still-life representing the "${niche}" world. The frame contains a single clean paper checklist or open notebook, a pen, and two to three emblematic physical objects pulled DIRECTLY from the real everyday kit of someone who works in or consumes "${niche}" (examples must match the niche: e.g. medical tools for healthcare, real-estate contracts for realtors, gardening tools for gardening, baby items for postpartum, kitchen tools for cooking). Matte textured surface, soft diffused morning light from the upper-left, minimalist and trustworthy. No people. Shot from directly above. Absolutely no travel items, no laptops, no iPhones, no coffee shop cues.`,
    low_ticket:       `NICHE-SPECIFIC close-up over-the-shoulder view of ${audience} in the specific everyday setting where "${niche}" actually happens (e.g. kitchen for cooking, gym for fitness, home office for PMP prep, clinic for healthcare, garden for gardening, classroom for teachers). They are mid-task, using ONE real physical object that belongs to "${niche}". Natural window light, candid editorial feel, photoreal. Clothing, props and background must clearly belong to the "${niche}" world — NOT travel, NOT digital-nomad, NOT generic home-office-with-laptop.`,
    premium:          `NICHE-SPECIFIC editorial portrait of ${audience} — a confident credible practitioner of "${niche}" — mid-action in the environment most associated with the niche. Subject looks focused (NOT smiling at camera). Clothing/uniform/gear appropriate to "${niche}" (e.g. scrubs for nurses, apron for chefs, chalkboard behind a teacher, running gear for runners, gardening gloves for gardeners). Warm cinematic light, shallow depth of field. Environmental cues explicitly evoke the "${niche}" world — no travel, no co-working, no beach, no airport.`,
    high_ticket:      `NICHE-SPECIFIC premium editorial wide shot of the most iconic environment for someone who has mastered "${niche}". Subject is a practitioner/expert in their authentic ${niche} uniform / gear / studio / workshop / field, styled to feel aspirational and magazine-cover polished. Gold-hour natural light, rich textures specific to this craft. This is NOT a travel or lifestyle photo — it is a portrait of mastery inside the niche's actual world.`,
  };

  const baseScene = TIER_SCENES[tierKey] || TIER_SCENES.low_ticket;

  // Lead with the niche + audience so the model CAN'T drift back to nomad defaults.
  const customScenePrompt = [
    `NICHE: ${niche}.`,
    `AUDIENCE: ${audience}.`,
    `EMOTIONAL TONE: grounded, credible, specific to this niche — not generic, not lifestyle, not travel.`,
    `SCENE DIRECTION: ${baseScene}`,
    `Product tier: ${tier.label} — ${tier.size}.`,
    `Brand cohesion across the 4 tiers: warm natural light, organic textures, editorial-not-corporate, photoreal (no illustration/stock-photo cliches). BUT every cover MUST look like it belongs to the "${niche}" world — no digital-nomad, beach, airport, boarding pass, passport, laptop-in-cafe, co-working, make-money-online, or travel visuals UNLESS the niche itself is literally about travel.`,
  ].join(" ");

  const tierSeed = { free_lead_magnet: 0, low_ticket: 1, premium: 2, high_ticket: 3 }[tierKey] ?? 0;

  let coverDataUrl = null;
  try {
    const { dataUrl } = await generateImage({
      apiKey,
      prompt: customScenePrompt,
      hook: title.slice(0, 64),
      size: "1024x1536",
      quality: "medium",
      variantSeed: tierSeed,
      mode: "custom",
    });
    coverDataUrl = dataUrl;
  } catch (e) {
    onProgress({ stage: "cover_error", label: `Cover skipped: ${e.message}`, progress: 90 });
  }

  onProgress({ stage: "done", label: `${tier.label} ready`, progress: 100 });

  return {
    tierKey,
    label: tier.label,
    title,
    markdown,
    cover: coverDataUrl,
  };
}

function extractTitle(md = "") {
  const m = md.match(/^\s*#\s+(.+)$/m);
  return m ? m[1].trim().replace(/[*_`]/g, "") : null;
}

// ─────────────────────────────────────────────────────────────────────
// Safety net — strip any placeholder syntax the model may have left in
// despite the explicit ban. Removes "[Your Name]"-style brackets, the
// matching byline lines, and other fill-in markers so the deliverable is
// always a finished asset.
// ─────────────────────────────────────────────────────────────────────
const PLACEHOLDER_PATTERNS = [
  // Bracketed forms: [Your Name], [Insert X], [Author], [Creator], [Brand], etc.
  /\[\s*(your|insert|author|creator|brand|company|client|name|email|url|website|logo|date|placeholder|tbd|todo)[^\]\n]{0,80}\]/gi,
  // Angle-bracket forms: <your name>, <insert email>
  /<\s*(your|insert|author|creator|brand|company|client|name|email|url|website|logo|date)[^>\n]{0,80}>/gi,
  // Bare TBD / TODO / Lorem ipsum
  /\b(TBD|TODO|Lorem ipsum)\b/gi,
];

function stripPlaceholders(md = "") {
  if (!md) return md;
  let out = md;
  // Drop entire byline lines that the model sometimes still emits despite the ban.
  out = out.replace(/^\s*by\s+\[[^\]\n]+\]\s*$/gim, "");
  out = out.replace(/^\s*by\s+<[^>\n]+>\s*$/gim, "");
  out = out.replace(/^\s*author:\s*\[[^\]\n]+\]\s*$/gim, "");
  // Strip any remaining bracketed/angled placeholder spans.
  for (const re of PLACEHOLDER_PATTERNS) {
    out = out.replace(re, "");
  }
  // Tidy up double-spaces / blank-line runs we may have introduced.
  out = out.replace(/[ \t]{2,}/g, " ");
  out = out.replace(/\n{3,}/g, "\n\n");
  return out.trim();
}
